import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

export function Page({
  title,
  description,
  actions,
  children,
}: {
  title: string;
  description?: string;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="mx-auto w-full max-w-6xl space-y-6">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">{title}</h1>
          {description && (
            <p className="mt-1 max-w-2xl text-sm text-muted-foreground">{description}</p>
          )}
        </div>
        {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
      </header>
      {children}
    </div>
  );
}

export function Panel({
  title,
  description,
  actions,
  children,
  className,
}: {
  title?: string;
  description?: string;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={cn("rounded-xl border bg-card shadow-sm", className)}>
      {(title || actions) && (
        <div className="flex items-start justify-between gap-3 border-b px-5 py-4">
          <div>
            {title && <h2 className="text-base font-semibold">{title}</h2>}
            {description && <p className="mt-0.5 text-sm text-muted-foreground">{description}</p>}
          </div>
          {actions}
        </div>
      )}
      <div className="p-5">{children}</div>
    </section>
  );
}

export function Stat({
  label,
  value,
  hint,
  attention,
}: {
  label: string;
  value: ReactNode;
  hint?: string;
  attention?: boolean;
}) {
  return (
    <div
      className={cn(
        "rounded-xl border bg-card px-4 py-4 shadow-sm",
        attention && "border-amber-200 bg-amber-50"
      )}
    >
      <p className="text-sm text-muted-foreground">{label}</p>
      <p className="mt-1 font-heading text-3xl font-semibold tabular-nums tracking-tight">{value}</p>
      {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

export function EmptyState({ title, body }: { title: string; body: string }) {
  return (
    <div className="rounded-xl border border-dashed bg-card px-6 py-10 text-center">
      <p className="font-medium">{title}</p>
      <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground">{body}</p>
    </div>
  );
}

export function QueryGate({
  loading,
  error,
  children,
}: {
  loading?: boolean;
  error?: string | null;
  children: ReactNode;
}) {
  if (loading) {
    return (
      <div className="space-y-3" aria-busy="true" aria-live="polite">
        <div className="h-8 w-48 animate-pulse rounded-md bg-muted" />
        <div className="h-28 animate-pulse rounded-xl bg-muted" />
        <div className="h-28 animate-pulse rounded-xl bg-muted" />
      </div>
    );
  }
  if (error) {
    return (
      <p className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">
        {error}
      </p>
    );
  }
  return children;
}

const OK = new Set(["approved", "completed", "active", "verified", "present"]);
const WAIT = new Set(["pending", "submitted", "late"]);
const BAD = new Set(["rejected", "overdue", "revoked", "absent", "cancelled", "canceled"]);

export function StatusBadge({ status }: { status?: string | null }) {
  const label = status || "—";
  const key = label.toLowerCase();
  const tone = OK.has(key)
    ? "bg-emerald-50 text-emerald-800 ring-emerald-200"
    : WAIT.has(key)
      ? "bg-amber-50 text-amber-900 ring-amber-200"
      : BAD.has(key)
        ? "bg-red-50 text-red-800 ring-red-200"
        : "bg-slate-100 text-slate-700 ring-slate-200";
  return (
    <span className={cn("inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ring-1 ring-inset", tone)}>
      {label.replace(/_/g, " ")}
    </span>
  );
}

export function DataTable({ rows }: { rows: Record<string, unknown>[] }) {
  if (!rows.length) {
    return <EmptyState title="No rows" body="There is nothing in this report yet." />;
  }
  const keys = Object.keys(rows[0]).filter((key) => typeof rows[0][key] !== "object").slice(0, 8);
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[32rem] text-left text-sm">
        <thead>
          <tr className="border-b text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {keys.map((key) => (
              <th key={key} className="px-3 py-2 font-medium">
                {key.replace(/_/g, " ")}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={String(row.id ?? index)} className="border-b last:border-0">
              {keys.map((key) => (
                <td key={key} className="px-3 py-2.5 align-top">
                  {row[key] == null || row[key] === "" ? "—" : String(row[key])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function initials(name?: string | null) {
  const parts = (name || "?").trim().split(/\s+/).slice(0, 2);
  return parts.map((part) => part[0]?.toUpperCase() ?? "").join("") || "?";
}
